import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td
} from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ButtonIcon,
  OutlineCard
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import {
  fetchJournalConfiguredBuckets
} from 'src/api/queries';

import BucketFormModal, { ModalMode } from './BucketFormModal';
import { ICOCConfig } from '@publishing-engine/domain/models';

// ---- Types ----

interface WorkFlowBucketsProps {
  journalId: string;
  cocConfig?: ICOCConfig[];
}

interface ModalState {
  isOpen: boolean;
  mode: ModalMode;
  editingRole?: string;
}

// ---- Constants ----

const INITIAL_MODAL_STATE: ModalState = {
  isOpen: false,
  mode: 'add'
};

// ---- Main component ----

const WorkFlowBuckets: React.FC<WorkFlowBucketsProps> = ({
  journalId,
  cocConfig
}) => {
  // ---- Modal state ----
  const [modal, setModal] = useState<ModalState>(INITIAL_MODAL_STATE);


  const availableRoles = useMemo(
    () => cocConfig?.map((r) => r.role) ?? [],
    [cocConfig],
  );

  const roleDisplayNames: Record<string, string> = useMemo(
    () =>
      Object.fromEntries(
        cocConfig?.map((r) => [r.role, r.roleAbbreviation || r.role]) ?? [],
      ),
    [cocConfig],
  );

  // ---- Fetch configured (saved) buckets for all roles (single call) ----
  const { data: configuredBucketsData, isLoading: isConfiguredBucketsLoading } =
    useQuery({
      queryKey: ['journal-configuration-buckets', journalId],
      queryFn: () => fetchJournalConfiguredBuckets(journalId),
      enabled: availableRoles.length > 0,
    });

  // Map role -> configured bucket names
  const configuredBucketsByRole = useMemo(() => {
    const result: Record<string, string[]> = {};
    if (configuredBucketsData) {
      for (const entry of configuredBucketsData) {
        result[entry.role] = entry.buckets.map(b => b.bucketName);
      }
    }
    return result;
  }, [configuredBucketsData]);

  // ---- Handlers ----

  const openModal = useCallback(
    (mode: ModalMode, editingRole?: string) => {
      setModal({ isOpen: true, mode, editingRole });
    },
    []
  );

  const closeModal = useCallback(() => {
    setModal(INITIAL_MODAL_STATE);
  }, []);

  const handleConfigure = useCallback(
    (role: string) => {
      openModal('edit', role);
    },
    [openModal]
  );

  // ---- Render ----

  return (
    <Flex flexDirection={'column'} gap={8}>
     <OutlineCard borderless paddingX={4} paddingY={6}>
        <Flex flexDirection={'column'} padding={2} gap={8}>
          <Text textStyle={'headingSection'}>Role Workflow Configurations</Text>

          {/* Table or empty state */}
          {availableRoles.length === 0 ? (
            <OutlineCard borderless paddingX={4} paddingY={6}>
              <Flex
                flexDirection={'column'}
                gap={2}
                alignItems="center"
                paddingY={8}
              >
                <Text textStyle="headingGroup">
                  No roles available
                </Text>
                <Text textStyle="textBody">
                  No workspace roles found. Create roles first before configuring
                  workflow buckets.
                </Text>
              </Flex>
            </OutlineCard>
          ) : (
            <Box overflowX="auto" width="100%">
              <Table>
                <Thead>
                  <Tr
                    data-testId="workflow-buckets-table-header"
                    sx={{
                      '& th': {
                        textTransform: 'none',
                        color: 'black',
                        background: 'white'
                      }
                    }}
                  >
                    <Th p={{ base: 2, md: 4 }}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Role
                      </Text>
                    </Th>
                    <Th p={{ base: 2, md: 4 }}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Configured Workflows
                      </Text>
                    </Th>
                    <Th width="100px" textAlign="center">
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Actions
                      </Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {availableRoles.map(role => {
                    const configured = configuredBucketsByRole[role] ?? [];
                    const displayName = roleDisplayNames?.[role] ?? role;

                    return (
                      <Tr
                        key={role}
                        data-testId="workflow-buckets-table-row"
                        background="white"
                        alignItems="center"
                        _last={{ borderBottom: 'none' }}
                      >
                        <Td px={{ base: 2, md: 6 }}>
                          <Text textStyle="headingItem">{displayName}</Text>
                        </Td>
                        <Td px={{ base: 2, md: 6 }}>
                          {isConfiguredBucketsLoading ? (
                            <Text textStyle="textContext" color="textSubtle">
                              Loading...
                            </Text>
                          ) : configured.length === 0 ? (
                            <Text textStyle="textContext" color="textSubtle">
                              No buckets configured
                            </Text>
                          ) : (
                            <Text textStyle="textContext">
                              {configured.join(', ')}
                            </Text>
                          )}
                        </Td>
                        <Td px={{ base: 2, md: 6 }}>
                          <ButtonIcon
                            variant="secondary"
                            size="small"
                            ariaLabel={`Configure buckets for ${displayName}`}
                            icon={<FontAwesomeIcon icon={faEdit} />}
                            onClick={() => handleConfigure(role)}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </Flex>
      </OutlineCard>

      {/* BucketFormModal — self-contained: saves/deletes internally */}
      {modal.isOpen && (
        <BucketFormModal
          isOpen={modal.isOpen}
          mode={modal.mode}
          journalId={journalId}
          editingRole={modal.editingRole}
          onClose={closeModal}
          roleDisplayNames={roleDisplayNames}
        />
      )}
    </Flex>
  );
};

export default WorkFlowBuckets;
